// src/hooks/useMapCenterAndSearch.js
import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { fetchApproxLocationFromIp } from "../services/geoipService";
import { getBestAvailableCurrentPosition } from "../utils/geo";

const LOCAL_STORAGE_CENTER_KEY = "mapCenter";
const GEOIP_BOOTSTRAP_DELAY_MS = 1200;

function isLatLng(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.lat === "number" &&
    typeof obj.lng === "number" &&
    Number.isFinite(obj.lat) &&
    Number.isFinite(obj.lng)
  );
}

function shouldPopulateSearchFromSource(source) {
  return source && source !== "none";
}

function getSearchLabelFromGeocodeResults(results = []) {
  if (!Array.isArray(results) || results.length === 0) return "";
  return results[0]?.formatted_address || "";
}

export default function useMapCenterAndSearch(
  mapRef,
  initialCenter = null,
  userLocation = null,
  options = {}
) {
  const geocoderRef = useRef(null);
  const reverseGeocodeRequestRef = useRef(0);

  const {
    ipFallbackEnabled = true,
    ipFallbackForce = false,
    ipFallbackOnlyWhenNoStoredCenter = true,
    permissionState = null, // "granted" | "denied" | "prompt" | "unsupported" | null
    ipFallbackZoom = 18,
  } = options;

  // Track center + how it was chosen. This helps us avoid persisting fallback centers.
  const [center, setCenter] = useState(null);
  const [mapCenterSource, setMapCenterSource] = useState("none");
  const [isResolvingInitialCenter, setIsResolvingInitialCenter] = useState(true);
  const centerSourceRef = useRef("none"); // "initial" | "storage" | "geoip" | "firestore" | "hard" | "gps" | "user" | "none"

  const [searchText, setSearchText] = useState("");

  const didApplyUserLocation = useRef(false);
  const didTryGeoIpAfterDenied = useRef(false);
  const didInitializeCenter = useRef(false);

  const setCenterWithSource = useCallback((pos, source) => {
    const nextSource = source || "none";
    centerSourceRef.current = nextSource;
    setMapCenterSource((prev) => (prev === nextSource ? prev : nextSource));
    setCenter((prev) => {
      if (
        isLatLng(prev) &&
        isLatLng(pos) &&
        prev.lat === pos.lat &&
        prev.lng === pos.lng
      ) {
        return prev;
      }
      return pos;
    });
  }, []);

  // Reverse geocode helper
  const reverseGeocode = useCallback((pos) => {
    if (!pos || !geocoderRef.current) return;
    const requestId = reverseGeocodeRequestRef.current + 1;
    reverseGeocodeRequestRef.current = requestId;

    geocoderRef.current.geocode({ location: pos }, (results, status) => {
      if (requestId !== reverseGeocodeRequestRef.current) return;

      if (status === "OK" && results && results[0]) {
        const nextLabel = getSearchLabelFromGeocodeResults(results);
        if (!nextLabel) return;
        setSearchText((prev) => (prev === nextLabel ? prev : nextLabel));
      }
    });
  }, []);

  const tryApplyGeoIp = useCallback(
    async ({ force = false, apply = true } = {}) => {
      if (!ipFallbackEnabled) return null;

      try {
        const data = await fetchApproxLocationFromIp({ force });

        if (
          data?.ok &&
          data?.found &&
          typeof data.lat === "number" &&
          typeof data.lng === "number"
        ) {
          const fromIp = { lat: data.lat, lng: data.lng };
          if (apply) {
            setCenterWithSource(fromIp, "geoip");
          }
          return fromIp;
        }

        return null;
      } catch (err) {
        console.warn("[MapCenter] GeoIP lookup failed:", err);
        return null;
      }
    },
    [ipFallbackEnabled, setCenterWithSource]
  );

  // One-time initializer. This decides the FIRST center.
  useEffect(() => {
    if (didInitializeCenter.current) return;

    let cancelled = false;
    const applyInitialCenter = (pos, source) => {
      if (cancelled) return false;
      setCenterWithSource(pos, source);
      didInitializeCenter.current = true;
      setIsResolvingInitialCenter(false);
      return true;
    };

    const init = async () => {
      // 1) URL/route-supplied center
      if (initialCenter && isLatLng(initialCenter)) {
        applyInitialCenter(initialCenter, "initial");
        return;
      }

      // 2) localStorage
      let hasStoredCenter = false;
      let storedCenter = null;

      try {
        const saved = localStorage.getItem(LOCAL_STORAGE_CENTER_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (isLatLng(parsed)) {
            hasStoredCenter = true;
            storedCenter = parsed;
          }
        }
      } catch (e) {
        console.warn("[MapCenter] failed to parse localStorage mapCenter:", e);
      }

      if (hasStoredCenter && storedCenter) {
        applyInitialCenter(storedCenter, "storage");
        return;
      }

      // 3) If real GPS location is already available, prefer it over GeoIP/config defaults.
      if (userLocation && isLatLng(userLocation)) {
        if (applyInitialCenter(userLocation, "gps")) {
          didApplyUserLocation.current = true;
        }
        return;
      }

      // 4) GeoIP approximate center (optional)
      const shouldTryIp =
        ipFallbackEnabled &&
        (!ipFallbackOnlyWhenNoStoredCenter || !hasStoredCenter);

      if (shouldTryIp) {
        const shouldDelayGeoIp =
          !userLocation &&
          permissionState !== "denied" &&
          permissionState !== "unsupported";

        if (shouldDelayGeoIp) {
          await new Promise((resolve) => {
            const timerId = setTimeout(resolve, GEOIP_BOOTSTRAP_DELAY_MS);
            if (cancelled) {
              clearTimeout(timerId);
              resolve();
            }
          });
        }

        if (cancelled) return;

        const ipPos = await tryApplyGeoIp({
          force: ipFallbackForce,
          apply: false,
        });
        if (ipPos) {
          applyInitialCenter(ipPos, "geoip");
          return;
        }
      }

      // 5) Firestore config default
      try {
        const snap = await getDoc(doc(db, "config", "app"));
        const data = snap.exists() ? snap.data() : {};
        const cfg = data?.defaultMapCenter;

        if (isLatLng(cfg)) {
          applyInitialCenter({ lat: cfg.lat, lng: cfg.lng }, "firestore");
          return;
        }

        console.warn(
          "[MapCenter] defaultMapCenter missing/invalid in Firestore. Using final fallback."
        );
      } catch (err) {
        console.warn("[MapCenter] failed to load defaultMapCenter from Firestore:", err);
      }

      // 6) final hard fallback
      const fallback = { lat: 40.7128, lng: -74.006 };
      applyInitialCenter(fallback, "hard");
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [
    initialCenter,
    userLocation,
    permissionState,
    ipFallbackEnabled,
    ipFallbackForce,
    ipFallbackOnlyWhenNoStoredCenter,
    setCenterWithSource,
    tryApplyGeoIp,
  ]);

  // Persist center to localStorage only when the user chose it.
  useEffect(() => {
    if (!center) return;

    const src = centerSourceRef.current;
    const shouldPersist = src === "user";

    if (!shouldPersist) return;

    try {
      localStorage.setItem(LOCAL_STORAGE_CENTER_KEY, JSON.stringify(center));
    } catch {
      // ignore
    }
  }, [center]);

  useEffect(() => {
    if (!center || !geocoderRef.current) return;
    if (!shouldPopulateSearchFromSource(centerSourceRef.current)) return;
    if (centerSourceRef.current === "user") return;

    reverseGeocode(center);
  }, [center, reverseGeocode]);

  // Common handler for any center change (user-driven)
  const onCenterChange = useCallback(
    (pos) => {
      if (!isLatLng(pos)) return;
      setCenterWithSource(pos, "user");
      reverseGeocode(pos);
    },
    [reverseGeocode, setCenterWithSource]
  );

  const applyPassiveCenter = useCallback(
    (pos, source = "gps") => {
      if (!isLatLng(pos)) return;
      setCenterWithSource(pos, source);
      reverseGeocode(pos);
    },
    [reverseGeocode, setCenterWithSource]
  );

  // Apply user location once, when permissions are granted
  useEffect(() => {
    // Preserve explicit destination centers passed through route state
    // (e.g. "View on map" from a capsule details page).
    if (initialCenter && isLatLng(initialCenter)) return;

    if (!userLocation) return;
    if (didApplyUserLocation.current) return;

    if (!isLatLng(userLocation)) return;

    setCenterWithSource(userLocation, "gps");
    didApplyUserLocation.current = true;
  }, [initialCenter, userLocation, setCenterWithSource]);

  // NEW: If permission becomes denied for a guest, attempt GeoIP once and recenter the map.
  // This is the behavior you asked for: after deny, we still try an approximate center.
  useEffect(() => {
    const shouldTry =
      permissionState === "denied" &&
      !didTryGeoIpAfterDenied.current &&
      !userLocation &&
      !initialCenter;

    if (!shouldTry) return;

    // If the user already has a meaningful center (from storage/user), don't override it.
    const src = centerSourceRef.current;
    const canOverride =
      src === "none" || src === "hard" || src === "firestore";

    if (!canOverride) {
      didTryGeoIpAfterDenied.current = true;
      return;
    }

    didTryGeoIpAfterDenied.current = true;

    (async () => {
      const ipPos = await tryApplyGeoIp({ force: true });
      if (!ipPos) return;

      if (mapRef?.current) {
        mapRef.current.panTo(ipPos);
        mapRef.current.setZoom(ipFallbackZoom);
      }

      // Treat as user center so it persists and downstream queries use it.
      onCenterChange(ipPos);
    })();
  }, [
    permissionState,
    userLocation,
    initialCenter,
    tryApplyGeoIp,
    mapRef,
    ipFallbackZoom,
    onCenterChange,
  ]);

  // Called when the map first loads
  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      geocoderRef.current = new window.google.maps.Geocoder();

      if (center && shouldPopulateSearchFromSource(centerSourceRef.current)) {
        reverseGeocode(center);
      }
    },
    [center, mapRef, reverseGeocode]
  );

  // NEW: Robust recenter that works even when location permission is denied.
  // Returns true if it moved the map, false otherwise.
  const recenter = useCallback(
    async ({ forceIp = false } = {}) => {
      const canTryFreshGps =
        !forceIp &&
        permissionState !== "denied" &&
        permissionState !== "unsupported";

      if (canTryFreshGps) {
        try {
          const freshPos = await getBestAvailableCurrentPosition();
          if (mapRef?.current) {
            mapRef.current.panTo(freshPos);
            mapRef.current.setZoom(18);
          }
          applyPassiveCenter(freshPos, "gps");
          return true;
        } catch (err) {
          console.warn("[MapCenter] fresh GPS recenter failed:", err);
        }
      }

      // If denied or no GPS available, attempt GeoIP recenter.
      if (permissionState === "denied" || permissionState === "unsupported" || forceIp) {
        const ipPos = await tryApplyGeoIp({ force: true });
        if (!ipPos) return false;

        if (mapRef?.current) {
          mapRef.current.panTo(ipPos);
          mapRef.current.setZoom(ipFallbackZoom);
        }
        applyPassiveCenter(ipPos, "geoip");
        return true;
      }

      // If permission is "prompt"/unknown, we do not force GeoIP here unless caller wants it.
      return false;
    },
    [
      permissionState,
      tryApplyGeoIp,
      mapRef,
      ipFallbackZoom,
      applyPassiveCenter,
    ]
  );

  return {
    center,
    searchText,
    setSearchText,
    mapCenterSource,
    onMapLoad,
    onCenterChange,
    applyPassiveCenter,
    recenter, // new
    isResolvingInitialCenter,
  };
}
